import { computed, nextTick, Ref, watch } from 'vue';
import { type VTextField } from 'vuetify/components';
import { useDisplay } from 'vuetify';

export interface BaseInputComponentProps {
    /**
     * Label for the input field
     */
    label?: string | undefined;

    /**
     * Placeholder for the input field
     */
    placeholder?: string | undefined;

    /**
     * Whether the input field is loading
     */
    loading?: boolean;

    /**
     * Primary color for input fields
     */
    color?: string;

    /**
     * Base color for inactive states
     */
    baseColor?: string;

    /**
     * Whether the component is disabled
     */
    disabled?: boolean;

    /**
     * Density of the component
     */
    density?: NonNullable<InstanceType<typeof VTextField>['$props']['density']>;

    /**
     * Variant of the input field
     */
    variant?: NonNullable<InstanceType<typeof VTextField>['$props']['variant']>;

    /**
     * Rounded of the input field
     * @default 'md'
     */
    rounded?: NonNullable<InstanceType<typeof VTextField>['$props']['rounded']>;

    /**
     * Class for the input field
     */
    inputClass?: string;

    /**
     * Whether to hide the details of the input field
     */
    hideDetails?: boolean;
}

export function useBaseInputComponent(props: BaseInputComponentProps) {
    const { platform } = useDisplay();
    const isMobileDevice = computed(() => platform.value.ios || platform.value.android);
    const isIOS = computed(() => platform.value.ios);

    const cssClasses = computed(() => ({
        'input-component--disabled': props.disabled,
        [`input-component--variant-${props.variant}`]: props.variant,
        [`input-component--density-${props.density}`]: props.density
    }));

    function watchInputClass(input: Ref<HTMLElement | undefined>) {
        const fieldEl = computed<HTMLDivElement | undefined>(() => input.value?.querySelector('.v-field') as HTMLDivElement);

        const applyInputClass = (value: string) => fieldEl.value?.classList.add(value);
        const removeInputClass = (value: string) => fieldEl.value?.classList.remove(value);

        // apply inputClass to the input field, and remove the old value when the inputClass changes
        const watcher = watch(
            () => props.inputClass,
            (value, oldValue) =>
                nextTick(() => {
                    if (oldValue) {
                        removeInputClass(oldValue);
                    }

                    if (value) {
                        applyInputClass(value);
                    }
                })
        );

        // this ensures that inputClass is retained when changes are made to the VField class attribute
        const mutationCallback = (mutationsList: MutationRecord[]) => {
            for (const mutation of mutationsList) {
                if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') {
                    return;
                }

                const target = mutation.target as HTMLElement;
                if (props.inputClass && !target.classList.contains(props.inputClass)) {
                    applyInputClass(props.inputClass);
                }
            }
        };
        const observer = new MutationObserver(mutationCallback);

        // start observing the fieldEl when it is mounted
        watch(fieldEl, value => {
            if (value) {
                observer.observe(value, { attributes: true });

                if (props.inputClass) {
                    applyInputClass(props.inputClass);
                }
            } else {
                watcher?.();
                observer.disconnect();
            }
        });
    }

    return {
        isMobileDevice,
        isIOS,
        cssClasses,
        watchInputClass
    };
}
